import { Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { Fetching } from "components/common/loading/fetching";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import { useMemo } from "react";
import { LabellingCostByLabellerEntry } from "src/store/api/generatedApi";

type LabellerCostRow = LabellingCostByLabellerEntry & { id: string };

const toRow = (entry: LabellingCostByLabellerEntry): LabellerCostRow => ({
  ...entry,
  id: entry.labeller_user_id,
});

type LabellerCostTableProps = Readonly<{
  entries: LabellingCostByLabellerEntry[];
  isLoading: boolean;
  isFetching: boolean;
}>;

export const LabellerCostTable = ({
  entries,
  isLoading,
  isFetching,
}: LabellerCostTableProps) => {
  const { t } = useTenantTranslation();
  const formatNumber1dp = useFormatNumber()(1);
  const formatNumber2dp = useFormatNumber()(2);

  const rows = useMemo(() => entries.map(toRow), [entries]);

  const columns = useMemo(
    (): GridColDef<LabellerCostRow>[] => [
      {
        field: "labeller_name",
        headerName: t("name"),
        width: 180,
      },
      {
        field: "total_footage_seconds",
        headerName: t("totalTimeLabelled"),
        type: "number",
        width: 140,
        valueGetter: (_value, row) => row.total_footage_seconds / 3600,
        valueFormatter: (value: number) => `${formatNumber1dp(value)}h`,
      },
      {
        field: "total_active_seconds",
        headerName: t("totalTimeSpentLabelling"),
        type: "number",
        width: 160,
        valueGetter: (_value, row) => row.total_active_seconds / 3600,
        valueFormatter: (value: number) => `${formatNumber1dp(value)}h`,
      },
      {
        field: "cost_ratio",
        headerName: t("labelHoursPerHour"),
        type: "number",
        width: 130,
        valueFormatter: (value?: number | null) =>
          value != null ? formatNumber2dp(value) : "\u2014",
      },
      {
        field: "session_count",
        headerName: t("totalSessions"),
        type: "number",
        width: 120,
      },
      {
        field: "first_session_date",
        headerName: t("firstSession"),
        width: 130,
      },
      {
        field: "last_session_date",
        headerName: t("lastSession"),
        width: 130,
      },
    ],
    [t, formatNumber1dp, formatNumber2dp]
  );

  return (
    <Stack sx={{ height: 300 }}>
      <Fetching fetching={isFetching} sx={{ flex: 1, overflow: "hidden" }}>
        <HotkeyCompliantDataGrid
          columns={columns}
          rows={rows}
          loading={isLoading}
          initialState={{
            sorting: {
              sortModel: [{ field: "cost_ratio", sort: "desc" }],
            },
          }}
          slotProps={{
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
          }}
          rowHeight={40}
          columnHeaderHeight={44}
          disableRowSelectionOnClick
        />
      </Fetching>
    </Stack>
  );
};
